import React, { useCallback, useEffect, useState } from 'react'
import { Checkbox, FormControlLabel, MenuItem, Select } from '@material-ui/core'
import Modal from '../../../../components/Modal'
import List, { ListColumn, ListOption, ListRow, ListSection } from '../../../../components/List'
import { mdiDelete } from '@mdi/js'
import { LinksTutoriaisListaType, LinksTutoriaisUsuarioPermissaoPastaType } from '../types'
import getPermissaoLista from '../scripts/getPermissaoLista'
import savePermissaoLista from '../scripts/savePermissaoLista'
import { useDispatch } from 'react-redux'

interface Props {
    open: boolean
    setOpen: (open: boolean) => void
    lista: LinksTutoriaisListaType | null
    idUsuarioAdmin: number
    onSalvo: () => void
}

const ConfigPermissaoListaModal: React.FC<Props> = ({ open, setOpen, lista, idUsuarioAdmin, onSalvo }) => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [usuarios, setUsuarios] = useState<LinksTutoriaisUsuarioPermissaoPastaType[]>([])
    const [usuariosDisponiveis, setUsuariosDisponiveis] = useState<{ id: number; nome: string; login: string }[]>([])
    const [usuarioAdicionar, setUsuarioAdicionar] = useState<string>('')

    const carregar = useCallback(async () => {
        if (!lista || !open) return
        setLoading(true)
        try {
            const dados = await getPermissaoLista(dispatch, lista.id, idUsuarioAdmin)
            setUsuarios(dados.usuarios ?? [])
            setUsuariosDisponiveis(dados.usuariosDisponiveis ?? [])
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }, [dispatch, lista, idUsuarioAdmin, open])

    useEffect(() => {
        carregar()
    }, [carregar])

    const togglePermissao = useCallback((idUsuario: number, campo: 'flagVisualizar' | 'flagEditarLista' | 'flagEditarItem') => {
        setUsuarios(prev => prev.map(u => {
            if (u.idUsuario !== idUsuario) return u
            return { ...u, [campo]: u[campo] === 1 ? 0 : 1 }
        }))
    }, [])

    const handleAdicionarUsuario = useCallback(() => {
        if (!usuarioAdicionar) return
        const usuario = usuariosDisponiveis.find(u => u.id === Number(usuarioAdicionar))
        if (!usuario) return
        setUsuarios(prev => [...prev, {
            idUsuario: usuario.id,
            nome: usuario.nome,
            login: usuario.login,
            flagVisualizar: 1,
            flagEditarLista: 0,
            flagEditarItem: 0,
        }])
        setUsuariosDisponiveis(prev => prev.filter(u => u.id !== usuario.id))
        setUsuarioAdicionar('')
    }, [usuarioAdicionar, usuariosDisponiveis])

    const handleRemoverUsuario = useCallback((idUsuario: number) => {
        const usuario = usuarios.find(u => u.idUsuario === idUsuario)
        if (usuario) {
            setUsuariosDisponiveis(prev => [...prev, { id: usuario.idUsuario, nome: usuario.nome, login: usuario.login }])
        }
        setUsuarios(prev => prev.filter(u => u.idUsuario !== idUsuario))
    }, [usuarios])

    const handleSalvar = useCallback(async () => {
        if (!lista) return
        try {
            await savePermissaoLista(dispatch, {
                idUsuarioAdmin,
                idLista: lista.id,
                usuarios: usuarios.map(u => ({
                    idUsuario: u.idUsuario,
                    flagVisualizar: u.flagVisualizar,
                    flagEditarLista: u.flagEditarLista,
                    flagEditarItem: u.flagEditarItem,
                })),
            })
            onSalvo()
            setOpen(false)
        } catch (error) {
            console.log(error)
        }
    }, [dispatch, lista, idUsuarioAdmin, usuarios, onSalvo, setOpen])

    return (
        <Modal width={800} open={open} setOpen={setOpen} title={`Configurar permissões — ${lista?.nome ?? ''}`}>
            {loading ? (
                <div style={{ padding: '16px' }}>Carregando...</div>
            ) : (
                <>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
                        <Select
                            variant="standard"
                            value={usuarioAdicionar}
                            onChange={e => setUsuarioAdicionar(e.target.value as string)}
                            displayEmpty
                            style={{ minWidth: '240px' }}
                        >
                            <MenuItem value="" disabled>Selecionar usuário</MenuItem>
                            {usuariosDisponiveis.map(u => (
                                <MenuItem key={u.id} value={u.id.toString()}>{u.nome}</MenuItem>
                            ))}
                        </Select>
                        <button className="default" type="button" onClick={handleAdicionarUsuario} disabled={!usuarioAdicionar}>
                            Adicionar usuário
                        </button>
                    </div>
                    <List name="perm-pasta">
                        <ListSection type="header">
                            <ListRow grid="1fr 90px 90px 90px 60px">
                                <ListColumn>Usuário</ListColumn>
                                <ListColumn>Visualizar</ListColumn>
                                <ListColumn>Editar pasta</ListColumn>
                                <ListColumn>Editar links</ListColumn>
                                <ListColumn></ListColumn>
                            </ListRow>
                        </ListSection>
                        <ListSection type="main">
                            {usuarios.length === 0 && (
                                <ListRow grid="1fr">
                                    <ListColumn>Nenhum usuário configurado nesta pasta.</ListColumn>
                                </ListRow>
                            )}
                            {usuarios.map(u => (
                                <ListRow key={u.idUsuario} grid="1fr 90px 90px 90px 60px">
                                    <ListColumn>{u.nome}</ListColumn>
                                    <ListColumn center>
                                        <Checkbox checked={u.flagVisualizar === 1} onChange={() => togglePermissao(u.idUsuario, 'flagVisualizar')} />
                                    </ListColumn>
                                    <ListColumn center>
                                        <Checkbox checked={u.flagEditarLista === 1} onChange={() => togglePermissao(u.idUsuario, 'flagEditarLista')} />
                                    </ListColumn>
                                    <ListColumn center>
                                        <Checkbox checked={u.flagEditarItem === 1} onChange={() => togglePermissao(u.idUsuario, 'flagEditarItem')} />
                                    </ListColumn>
                                    <ListColumn>
                                        <ListOption icon={mdiDelete} status="error" onClick={() => handleRemoverUsuario(u.idUsuario)} />
                                    </ListColumn>
                                </ListRow>
                            ))}
                        </ListSection>
                    </List>
                    <div className="modalBoxMainActionsContainer">
                        <button className="outline" onClick={() => setOpen(false)}>Cancelar</button>
                        <button className="default" onClick={handleSalvar}>Salvar</button>
                    </div>
                </>
            )}
        </Modal>
    )
}

export default ConfigPermissaoListaModal
